import type { NoteEventAction } from './loggerActions';
import type { LoggerDraft, NoteEventDraft } from './loggerDraftTypes';

// Timeline note-event sub-reducer (Solution C). Owns the three note mutations
// so loggerNestedReducer stays under the 500-line budget — it delegates here
// via a single early-return guard. Pure; notes live on LoggerDraft.noteEvents
// (absent reads as []), entirely separate from entries[].sets[] so a note
// edit can never touch a logged set.
export function noteEventReducer(
  state: LoggerDraft,
  action: NoteEventAction,
): LoggerDraft {
  const events = state.noteEvents ?? [];
  switch (action.type) {
    case 'add_note_event':
      return { ...state, noteEvents: [...events, action.note] };
    case 'update_note_event':
      return {
        ...state,
        noteEvents: events.map((n) =>
          n.id === action.id ? { ...n, text: action.text } : n,
        ),
      };
    case 'remove_note_event':
      return {
        ...state,
        noteEvents: events.filter((n) => n.id !== action.id),
      };
  }
}

/** What saving does to the notes, in the order it does it: the saved notes
 *  the athlete removed are deleted, then each event is updated (its saved
 *  text changed), left alone, or created. One rule for the save
 *  (useFlushNoteEvents) and the ✕ (isEditChanged). */
export type NoteChange =
  | { kind: 'delete'; id: string }
  | { kind: 'update'; event: NoteEventDraft }
  | { kind: 'create'; event: NoteEventDraft };

export function noteChanges(
  saved: readonly { id: string; text: string }[],
  events: readonly NoteEventDraft[],
): NoteChange[] {
  const savedText = new Map(saved.map((n) => [n.id, n.text] as const));
  const kept = new Set(events.map((ev) => ev.id));
  const out: NoteChange[] = [];
  for (const id of savedText.keys()) if (!kept.has(id)) out.push({ kind: 'delete', id });
  for (const event of events) {
    const text = savedText.get(event.id);
    if (text === undefined) out.push({ kind: 'create', event });
    else if (text !== event.text) out.push({ kind: 'update', event });
  }
  return out;
}
